import React, { useMemo } from 'react';
import { Link } from 'react-router';

import { Paper, withStyles, WithStyles } from '@material-ui/core';
import { PaperProps } from '@material-ui/core/Paper';
import { get } from 'lodash';

import { Typography } from 'components/v2';
import useAccessControlItems from 'screens/MissionControl/hooks/useAccessControlItems';

import { useStyles } from './Breadcrumb.useStyles';
import { BreadcrumbStep, getBreadcrumbSteps, getSettingsMenuMap } from './Breadcrumb.utils';
import BreadcrumbSelect from './BreadcrumbSelect';

export interface BreadcrumbProps extends WithStyles<typeof useStyles>, Omit<PaperProps, 'classes'> {
  pathname: string;
  route: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ classes, pathname, route, ...props }) => {
  const accessControlItems = useAccessControlItems();
  const isNewFormBuilder = /v2\/form-builder/.test(pathname);
  const isNewFollowupWorkflowBuilder = /v2\/settings\/followup-workflows\/.+/.test(pathname);
  const isNewBroadcastTemplateBuilder = /v2\/settings\/utilities\/broadcast-templates/.test(pathname);

  const normalizedRoute = React.useMemo(() => {
    if (isNewFormBuilder) return '/v2/settings/utilities/(:variant)-builder';
    if (isNewFollowupWorkflowBuilder) return '/v2/settings/followup-workflows/builder';
    if (isNewBroadcastTemplateBuilder) return '/v2/settings/utilities/broadcast-templates';
    return route;
  }, [isNewFormBuilder, isNewFollowupWorkflowBuilder, isNewBroadcastTemplateBuilder, route]);

  const normalizedPathname = React.useMemo(() => {
    if (isNewFormBuilder) return '/v2/settings/utilities/form-builder';
    if (isNewFollowupWorkflowBuilder) return '/v2/settings/followup-workflows/builder';
    if (isNewBroadcastTemplateBuilder) return '/v2/settings/utilities/broadcast-templates';
    return pathname;
  }, [isNewFormBuilder, isNewFollowupWorkflowBuilder, isNewBroadcastTemplateBuilder, pathname]);

  const settingsMenuMap = useMemo(() => getSettingsMenuMap(accessControlItems), [accessControlItems]);
  const breadcrumbArray = useMemo(() => getBreadcrumbSteps(normalizedPathname, normalizedRoute, settingsMenuMap), [
    normalizedPathname,
    normalizedRoute,
    settingsMenuMap,
  ]);
  const stepHasMenu = (step: BreadcrumbStep) => get(step, 'menu.length') > 0;

  return (
    <Paper className={classes.paperRoot} square elevation={0} {...props}>
      {breadcrumbArray.map((step: BreadcrumbStep, i) => (
        <div key={step.label} className={classes.stepSection}>
          {i > 0 && (
            <Typography variant="h3W500F15" className={classes.listItemText}>
              {'>'}
            </Typography>
          )}
          {stepHasMenu(step) && <BreadcrumbSelect {...step} />}
          {!stepHasMenu(step) && step.path !== '' && (
            <Link to={step.label === 'Form Builder' ? '/v2/form-builder' : step.path} className={classes.link}>
              <Typography variant="h3W500F15" className={classes.listItemText}>
                {step.label}
              </Typography>
            </Link>
          )}
          {!stepHasMenu(step) && step.path === '' && (
            <Typography variant="h3W500F15" className={classes.listItemText}>
              {step.label}
            </Typography>
          )}
        </div>
      ))}
    </Paper>
  );
};

export default withStyles(useStyles)(Breadcrumb);
